/**
 * MobileFieldView — the mobile Roster Optimizer Field View (DD-RO-MOBILE-FIELD-0728).
 *
 * Approved design: Option 1 "pinned diamond". The diamond is FIXED at the top (~300px) and the
 * detail + swap options scroll underneath it, so tapping player → player never requires scrolling
 * back up to reach the field.
 *
 * §14.0 mobile-native: shares only the backend. No desktop-component import — the desktop Field
 * View (OptimizerDashboard/OptimizerPlayerTile/OptimizerFlyout) is read for BEHAVIOR only; every
 * style value here comes from ./optimizerTokens (→ dynastyTokens). No hex literals, no one-offs.
 *
 * DATA (all real; nothing fabricated):
 *   · roster        — leaguesAPI.getMyRosterCanonical / getTeamRosterCanonical (what MobileRoster uses)
 *   · alternatives  — leaguesAPI.getOptimizerAlternatives(leagueId, playerId, teamId, mode) → { alternatives[]
 *                     (FA), target_player } — the FA side the desktop Field View calls on player-tap.
 *   · trades        — leaguesAPI.getTargetedTrades(leagueId, teamId, playerId, 'send', mode)
 *                     .trade_engine.trade_sequences — the subject-anchored trades (desktop-flyout parity,
 *                     DD-RO-MOBILE-FIELD-0728). NOT a subject_trades field on getOptimizerAlternatives.
 *   · gains/bands   — alt.score.weighted_score ?? total_points_delta; trade pkg.net_value +
 *                     pkg.acceptance_probability. Real engine values — never placeholders.
 *
 * SEAM (CD owns presentation + routing; CC owns swap entry/execution — DD-RO-MOBILE-SWAP-0727):
 *   "Swap →" emits onRequestSwap({ player, drop }) — CC's player-detail → MobileDropDesignationModal
 *   → MobileFASwapSheet entry. If that entry hasn't landed, we fall back to opening MobileFASwapSheet
 *   directly with the same { player, drop } payload, which executes via the canonical
 *   generatePlanForDrop → executePlan. NO new mutation path is introduced here.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { leaguesAPI } from '../../../../services/apiService';
import MobilePlayerLink from '../../components/MobilePlayerLink';
import MobileFASwapSheet from './MobileFASwapSheet';
import {
  C, SP, FS, R, PRIMARY, BORDER, BORDER_SOFT, NUM, withAlpha,
  tierColor, sectionHeaderStyle, pillStyle,
} from './optimizerTokens';

// ============================================================================
// LOCKED FIELD GEOMETRY — must stay byte-identical to the approved mock
// (design_handoff: "Mobile Field View LOCKED.dc.html"). Canvas 358 x 390.
// Marker centering is transform:translate(-50%,-17px) ONLY.
// DO NOT add a negative margin-left — it double-centers and shifts 33px left.
// ============================================================================
export const FIELD_CANVAS = { w: 358, h: 390 };

// Base paths: a PERFECT square rotated 45deg — half-diagonal 58 on BOTH axes
// about centre (179,242).
export const DIAMOND_PATH = 'M179 184 L237 242 L179 300 L121 242 Z';
export const INFIELD_ARC  = 'M15 174 Q179 18 345 174';   // infield edge; no foul lines
export const MOUND        = { cx: 179, cy: 242, rx: 17, ry: 13 };
export const SECOND_BASE  = { x: 174, y: 179, size: 10, rotate: 45, ox: 179, oy: 184 };
export const HOME_PLATE   = 'M172 300 L186 300 L186 307 L179 312 L172 307 Z';

// Slot coordinates (marker CENTRE, px in canvas space).
// Outfielders sit ABOVE the arc; infielders BETWEEN the base paths and the arc.
export const HITTER_SLOTS = [
  { slot: 'LF', x: 42,  y: 86  },
  { slot: 'CF', x: 128, y: 52  },
  { slot: 'RF', x: 230, y: 52  },
  { slot: 'OF', x: 316, y: 86  },
  { slot: 'SS', x: 132, y: 154 },
  { slot: '2B', x: 226, y: 154 },
  { slot: '3B', x: 98,  y: 220 },
  { slot: '1B', x: 260, y: 220 },
  { slot: 'C',  x: 179, y: 336 },   // crouch, clear of the plate (y300-312)
];

// Bench strip: BARE markers in a flex row, one hairline above and below.
// NO container box. Order and contents are generated from league roster settings.
export const BENCH_SLOTS_FALLBACK = ['UTIL', 'MI', 'C', 'CI'];

// Pitchers: NO diamond. Two textured clay lanes, five pitchers each.
// Rubber and plate are rotated 90deg to the left-to-right throwing line.
export const PITCH_LANES = [
  { y: 44,  centreY: 78  },
  { y: 168, centreY: 202 },
];
export const PITCH_LANE_BOX = { x: 10, w: 338, h: 68 };
export const PITCH_SLOT_X   = [70, 128, 186, 244, 299];
/* ------------------------------------------------------------------ *
 * Acceptance BANDS — same cutoffs as the desktop's single acceptance
 * scale (ACCEPT_GREEN 0.4 == backend _BEST_OVERALL_MIN_ACCEPTANCE,
 * ACCEPT_AMBER 0.2). Mobile shows the BAND; the precise % is detail-only.
 * Redeclared locally rather than importing desktop optimizerUtils (§14.0).
 * ------------------------------------------------------------------ */
export const ACCEPT_GREEN = 0.4;
export const ACCEPT_AMBER = 0.2;
export function acceptanceBand(pct) {
  if (pct == null) return null;
  if (pct >= ACCEPT_GREEN) return { label: 'likely', color: C.success };
  if (pct >= ACCEPT_AMBER) return { label: 'coin-flip', color: C.warning };
  return { label: 'unlikely', color: C.error };
}

/* Strip the slot suffix: "SS_0" → "SS", "OF_1" → "OF". */
export const basePosition = (pos) => (pos ? String(pos).replace(/_\d+$/, '') : null);

/* Slot → pixel centre, straight off the LOCKED exported constants. */
const SLOT_XY = HITTER_SLOTS.reduce((m, s0) => { m[s0.slot] = { x: s0.x, y: s0.y }; return m; }, {});
/* The four LOCKED outfield centres, in order, for OF_n slots with no LF/CF/RF naming. */
const OF_SLOT_ORDER = ['LF', 'CF', 'RF', 'OF'];

/** Status dot — same semantics as the desktop tile's indicator dot, via the tier tokens. */
function statusDotColor(player) {
  const st = player?.league_context?.optimizer?.status
    || player?.optimizer_status
    || null;
  if (st) return tierColor(st);
  return C.info;
}

function playerName(p) {
  return p?.info?.full_name
    || [p?.info?.first_name, p?.info?.last_name].filter(Boolean).join(' ')
    || '—';
}
function playerPos(p) {
  return p?.eligibility_display
    || basePosition(p?.league_context?.roster?.position)
    || p?.info?.position || '—';
}
function playerSalary(p) {
  const sal = p?.league_context?.financial?.contract_salary;
  return sal != null ? `$${sal}` : '';
}

/* Stat formatting — mirrors the desktop's fmtStatValue rules. */
const RATE_3 = new Set(['AVG', 'OBP', 'SLG', 'OPS']);
const RATE_2 = new Set(['ERA', 'WHIP']);
export function fmtStatValue(abbrev, value) {
  if (value == null) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  if (RATE_3.has(abbrev)) return n.toFixed(3).replace(/^0/, '');
  if (RATE_2.has(abbrev)) return n.toFixed(2);
  return String(Math.round(n));
}

/** DD-RO-MOBILE-DETAIL-POLISH-0728: the selected-player stat line — a FIXED display set (abbrev → season
 * field), NOT the league scoring categories. The Field view's meta (from /gaps) carries no `categories`, so
 * the old meta-driven line rendered EMPTY there; this always shows the standard line straight off season stats. */
const DISPLAY_STATS = {
  hitting: [['R', 'runs'], ['HR', 'home_runs'], ['RBI', 'rbi'], ['SB', 'stolen_bases'], ['AVG', 'batting_avg'], ['OPS', 'ops']],
  pitching: [['W', 'wins'], ['SV', 'saves'], ['K', 'strikeouts_pitched'], ['ERA', 'era'], ['WHIP', 'whip'], ['QS', 'quality_starts']],
};
function displayStats(player, isPitcherSide) {
  const list = isPitcherSide ? DISPLAY_STATS.pitching : DISPLAY_STATS.hitting;
  const bucket = (player?.stats?.season || {})[isPitcherSide ? 'pitching' : 'hitting'] || {};
  return list.map(([abbrev, field]) => ({ abbrev, formatted: fmtStatValue(abbrev, bucket[field]) }));
}

const PITCHER_SLOTS = /^(P|SP|RP)$/;

export default function MobileFieldView({
  leagueId,
  teamId,
  mode = 'balanced',
  side = 'hitters',              // 'hitters' | 'pitchers' — driven by the parent's segmented control
  meta,                          // trades/gaps meta: { categories, stat_field_mapping }
  onRequestSwap,                 // CC's entry: ({ player, drop }) => void
  onPlayerClick,
  onComplete,                    // re-fetch the optimizer after a completed swap
}) {
  const [roster, setRoster] = useState(null);
  const [rosterError, setRosterError] = useState(null);
  const [selected, setSelected] = useState(null);          // the tapped roster player
  const [alts, setAlts] = useState(null);                  // getOptimizerAlternatives response
  const [altsLoading, setAltsLoading] = useState(false);
  const [altsError, setAltsError] = useState(null);
  const [tab, setTab] = useState('free_agents');           // 'free_agents' | 'trades'
  const [fallbackRec, setFallbackRec] = useState(null);    // only if CC's entry isn't wired

  /* ---- roster (the same canonical source MobileRoster uses) ---- */
  useEffect(() => {
    if (!leagueId) return undefined;
    let alive = true;
    const req = teamId
      ? leaguesAPI.getTeamRosterCanonical(leagueId, teamId)
      : leaguesAPI.getMyRosterCanonical(leagueId);
    req
      .then((res) => { if (alive) setRoster(res?.players || []); })
      .catch((e) => { if (alive) setRosterError(e?.message || 'roster load failed'); });
    return () => { alive = false; };
  }, [leagueId, teamId]);

  /* ---- per-player 1-1 suggestions (the desktop Field View's player-tap source) ---- */
  const loadAlternatives = useCallback((player) => {
    const pid = player?.ids?.mlb;
    if (!leagueId || pid == null) return;
    setAltsLoading(true);
    setAltsError(null);
    setAlts(null);
    // DD-RO-MOBILE-FIELD-0728: the Trades tab reads the SAME two sources the desktop flyout does
    // (OptimizerDashboard:142-153) — subject-ANCHORED send trades from getTargetedTrades(...,'send') merged
    // as `subject_trades` (+ its `subject_trades_gate` for the honest withheld state), alongside the FA
    // alternatives. NOT a `subject_trades` field on getOptimizerAlternatives (which serves `trade_targets`, a
    // different shape the backend never returns here). allSettled so a targeted failure never blanks the FAs.
    Promise.allSettled([
      leaguesAPI.getOptimizerAlternatives(leagueId, pid, teamId, mode),
      leaguesAPI.getTargetedTrades(leagueId, teamId, pid, 'send', mode),
    ]).then(([altRes, sendRes]) => {
      if (altRes.status !== 'fulfilled') {
        setAltsError(altRes.reason?.message || 'Couldn’t load swap options.');
        setAltsLoading(false);
        return;
      }
      // Each element CD's render reads FLAT (pkg.net_value / pkg.acceptance_probability); getTargetedTrades
      // returns SEQUENCES with the display fields inside trade_package — flatten so the render stays verbatim.
      const subjectTrades = sendRes.status === 'fulfilled'
        ? (sendRes.value?.trade_engine?.trade_sequences || []).map((seq) => ({
            id: seq.sequence_id,
            total_standings_gain: seq.total_standings_gain,
            ...(seq.trade_package || {}),
          }))
        : [];
      const subjectTradesGate = sendRes.status === 'fulfilled'
        ? (sendRes.value?.trade_engine?.gate || null) : null;
      setAlts({ ...(altRes.value || {}), subject_trades: subjectTrades, subject_trades_gate: subjectTradesGate });
      setAltsLoading(false);
    });
  }, [leagueId, teamId, mode]);

  const selectPlayer = useCallback((player) => {
    setSelected(player);
    setTab('free_agents');
    loadAlternatives(player);
  }, [loadAlternatives]);

  /* re-run suggestions when the mode changes (mode moves output) */
  useEffect(() => {
    if (selected) loadAlternatives(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  /* ---- ACTIVE LINEUP ONLY — NO BENCH. Bench (status !== 'active') is failsafe-only in this league and is
     NEVER rendered; the mockup has no bench strip (ruling: exclude all bench, no exceptions). ---- */
  const { fieldPlayers, benchPlayers } = useMemo(() => {
    const active = (roster || []).filter((p) => {
      const status = p?.league_context?.roster?.status;
      if (status !== 'active') return false;                       // exclude ALL bench, period
      const slot = basePosition(p?.league_context?.roster?.position);
      const pitcher = slot ? PITCHER_SLOTS.test(slot) : false;
      return side === 'pitchers' ? pitcher : !pitcher;
    });

    let ofIdx = 0;
    const placed = [];
    const overflow = [];
    // Pitchers: no diamond at all — five per clay lane, in roster order. No bench.
    if (side === 'pitchers') {
      const lanes = active.map((p, i) => ({
        player: p,
        slot: basePosition(p?.league_context?.roster?.position) || 'P',
        xy: {
          x: PITCH_SLOT_X[i % PITCH_SLOT_X.length],
          y: (PITCH_LANES[Math.floor(i / PITCH_SLOT_X.length)] || PITCH_LANES[PITCH_LANES.length - 1]).centreY,
        },
      }));
      return { fieldPlayers: lanes, benchPlayers: [] };
    }
    active.forEach((p) => {
      const slot = basePosition(p?.league_context?.roster?.position);
      if (slot === 'OF') {
        // spread OF_n across the four LOCKED outfield centres, in slot order
        const key = OF_SLOT_ORDER[ofIdx] || OF_SLOT_ORDER[OF_SLOT_ORDER.length - 1];
        ofIdx += 1;
        placed.push({ player: p, slot: 'OF', xy: SLOT_XY[key] });
      } else if (slot && SLOT_XY[slot]) {
        placed.push({ player: p, slot, xy: SLOT_XY[slot] });
      } else {
        overflow.push({ player: p, slot: slot || playerPos(p) });   // ACTIVE utility only (DH/UT/MI/CI) — never bench
      }
    });
    // DD-RO-MOBILE-TOPBAR-0728: co-located markers (e.g. two active catchers on the C slot) must sit SIDE BY
    // SIDE — same vertical position, spread only HORIZONTALLY, symmetric about the slot's x — not stacked.
    const byXY = {};
    placed.forEach((m) => { const k = `${m.xy.x},${m.xy.y}`; (byXY[k] = byXY[k] || []).push(m); });
    Object.values(byXY).forEach((group) => {
      if (group.length < 2) return;
      const cx = group[0].xy.x, cy = group[0].xy.y, gap = 42;   // ~34px markers + a hair of air
      const start = -((group.length - 1) / 2) * gap;
      group.forEach((m, i) => { m.xy = { x: cx + start + i * gap, y: cy }; });   // new object — never mutate SLOT_XY
    });
    // ACTIVE-only: the second row holds active non-diamond slots (DH/UT/MI/CI). NO bench players ever.
    return { fieldPlayers: placed, benchPlayers: overflow };
  }, [roster, side]);

  /* ---- the swap seam ---- */
  const requestSwap = useCallback((candidatePlayer, scoreBlock) => {
    if (!selected || !candidatePlayer) return;
    const payload = {
      player: candidatePlayer,                                  // carries ids.mlb
      drop: {
        player_id: selected?.ids?.mlb,
        player_name: playerName(selected),
        position: playerPos(selected),
        eligibility_display: selected?.eligibility_display,
        roster_status: selected?.league_context?.roster?.status,
        salary: selected?.league_context?.financial?.contract_salary,
      },
    };
    if (onRequestSwap) { onRequestSwap(payload); return; }
    // CC's entry not wired yet → same payload straight to the sheet (canonical execution path).
    setFallbackRec({
      ...payload,
      net_category_effect: (scoreBlock?.category_deltas || []).map((cd) => ({
        category: cd.category,
        stat_delta: cd.stat_delta,
        rank_before: cd.rank_before,
        rank_after: cd.rank_after,
        points_delta: cd.points_delta,
      })),
      score: scoreBlock || null,
    });
  }, [selected, onRequestSwap]);

  const shs = sectionHeaderStyle(PRIMARY);
  const isPitcherSide = side === 'pitchers';
  // DD-RO-MOBILE-TOPBAR-0728: the field SCALES to device width. The locked geometry is authored in a
  // FIELD_CANVAS.w × canvasH space; the container is width:100% (capped at the design width) with that aspect
  // ratio, the SVG uses a viewBox at 100%, and markers are positioned as a PERCENT of the canvas — so the
  // rightmost OF (left 316/358 = 88%) is always fully on-field, never clipped, at any width.
  const canvasH = isPitcherSide ? 290 : FIELD_CANVAS.h;
  const pctX = (x) => `${(x / FIELD_CANVAS.w) * 100}%`;
  const pctY = (y) => `${(y / canvasH) * 100}%`;
  const detailPlayer = alts?.target_player || selected;
  // DD-RO-MOBILE-DETAIL-POLISH-0728: the stat line reads the ONE canonical source — the tapped roster player
  // (`selected`), whose season stats come straight from the roster-canonical fetch. No source fallback (Rule 9):
  // if that bucket is empty, the line shows dashes honestly rather than papering over it with another source.

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, ...NUM }}
         data-testid="mobile-field-view">

      {/* ============ PINNED FIELD — LOCKED geometry ============ */}
      <div style={{
        flexShrink: 0, position: 'relative',
        width: '100%', maxWidth: FIELD_CANVAS.w, margin: '0 auto',
        aspectRatio: `${FIELD_CANVAS.w} / ${canvasH}`,
        overflow: 'hidden',
        background: `radial-gradient(120% 90% at 50% 20%, ${withAlpha(C.success, 0.16)} 0%, ${withAlpha(C.success, 0.09)} 55%, ${withAlpha(C.success, 0.05)} 100%), ${C.neutral[950]}`,
      }} data-testid={isPitcherSide ? 'field-lanes' : 'field-diamond'}>

        {isPitcherSide ? (
          /* ---- PITCHERS: two textured clay lanes, rubber + plate rotated 90deg ---- */
          <svg viewBox={`0 0 ${FIELD_CANVAS.w} 290`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet"
               style={{ position: 'absolute', inset: 0 }} aria-hidden="true">
            <defs>
              <filter id="mfvDirt" x="0" y="0" width="100%" height="100%">
                <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="4" seed="7" result="n" />
                <feColorMatrix in="n" type="saturate" values="0" />
              </filter>
              <linearGradient id="mfvClay" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#7d5c3c" />
                <stop offset="1" stopColor="#63462c" />
              </linearGradient>
            </defs>
            {PITCH_LANES.map((lane) => (
              <g key={lane.y}>
                <rect x={PITCH_LANE_BOX.x} y={lane.y} width={PITCH_LANE_BOX.w} height={PITCH_LANE_BOX.h} fill="url(#mfvClay)" />
                <rect x={PITCH_LANE_BOX.x} y={lane.y} width={PITCH_LANE_BOX.w} height={PITCH_LANE_BOX.h} fill="#000" opacity="0.30" filter="url(#mfvDirt)" />
                <rect x={PITCH_LANE_BOX.x} y={lane.y} width={PITCH_LANE_BOX.w} height={PITCH_LANE_BOX.h} fill="none" stroke={withAlpha(C.black, 0.40)} strokeWidth="1" />
                {/* rubber — tall slab, turned 90deg to the throwing line */}
                <rect x="25" y={lane.centreY - 13} width="8" height="26" fill="#f2f2f4" />
                {/* plate — flat edge to the pitcher, point to the right */}
                <path d={`M323 ${lane.centreY - 12} L333 ${lane.centreY - 12} L342 ${lane.centreY} L333 ${lane.centreY + 12} L323 ${lane.centreY + 12} Z`} fill="#f2f2f4" />
                <path d={`M37 ${lane.centreY} L319 ${lane.centreY}`} stroke={withAlpha(C.white, 0.16)} strokeWidth="1" strokeDasharray="5 7" fill="none" />
              </g>
            ))}
          </svg>
        ) : (
          /* ---- HITTERS: contained arc, square base paths, mound, 2B bag, plate ---- */
          <svg viewBox={`0 0 ${FIELD_CANVAS.w} ${FIELD_CANVAS.h}`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet"
               style={{ position: 'absolute', inset: 0 }} aria-hidden="true">
            <path d={INFIELD_ARC} fill="none" stroke={withAlpha(C.white, 0.16)} strokeWidth="1.5" />
            <path d={DIAMOND_PATH} fill="none" stroke={PRIMARY} strokeWidth="2" />
            <ellipse cx={MOUND.cx} cy={MOUND.cy} rx={MOUND.rx} ry={MOUND.ry} fill={withAlpha(C.white, 0.05)} />
            <rect x={SECOND_BASE.x} y={SECOND_BASE.y} width={SECOND_BASE.size} height={SECOND_BASE.size}
                  transform={`rotate(${SECOND_BASE.rotate} ${SECOND_BASE.ox} ${SECOND_BASE.oy})`} fill="#e8e8ea" />
            <path d={HOME_PLATE} fill={withAlpha(C.white, 0.10)} />
          </svg>
        )}

        {fieldPlayers.map(({ player, slot, xy }) => {
          const active = selected?.ids?.mlb != null && selected.ids.mlb === player?.ids?.mlb;
          return (
            <button
              key={player?.ids?.mlb || `${slot}-${playerName(player)}`}
              type="button"
              onClick={() => selectPlayer(player)}
              data-testid="field-marker"
              aria-pressed={active}
              style={{
                position: 'absolute', top: pctY(xy.y), left: pctX(xy.x),
                transform: 'translate(-50%, -17px)',      /* ONLY — never a negative margin */
                background: 'none', border: 'none',
                padding: 0, cursor: 'pointer', textAlign: 'center', zIndex: 10,
              }}
            >
              <span style={{
                position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: active ? 38 : 34, height: active ? 38 : 34, margin: '0 auto',
                borderRadius: '50%',
                background: active ? PRIMARY : withAlpha(C.neutral[800], 0.95),
                border: `${active ? 2 : 1}px solid ${active ? PRIMARY : withAlpha(C.neutral[700], 0.5)}`,
                boxShadow: active ? `0 0 0 4px ${withAlpha(PRIMARY, 0.18)}` : 'none',
                color: active ? C.neutral[950] : C.neutral[300],
                fontSize: active ? FS.xs : FS['2xs'], fontWeight: active ? 900 : 800,
              }}>
                {slot}
                {!active && (
                  <span style={{
                    position: 'absolute', top: -2, right: -2, width: 8, height: 8,
                    borderRadius: '50%', background: statusDotColor(player),
                  }} />
                )}
              </span>
              <span style={{
                display: 'block', marginTop: 2, fontSize: FS['2xs'], whiteSpace: 'nowrap',
                color: active ? PRIMARY : C.neutral[300], fontWeight: active ? 700 : 400,
              }}>
                {(playerName(player).split(' ').pop()) || '—'}
              </span>
            </button>
          );
        })}

      </div>

      {/* ---- ACTIVE UTILITY (DH/UT/MI/CI) — active non-diamond lineup slots ONLY. NEVER bench. Bare markers,
              one hairline above and below, no container box. ---- */}
      {benchPlayers.length > 0 && (
        <div style={{
          flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
          gap: SP.sm, padding: `${SP.xs} ${SP.sm}`,
          borderTop: BORDER, borderBottom: BORDER,
        }} data-testid="util-row">
          {benchPlayers.map(({ player, slot }) => {
            const active = selected?.ids?.mlb != null && selected.ids.mlb === player?.ids?.mlb;
            return (
              <button key={player?.ids?.mlb || `${slot}-${playerName(player)}`} type="button"
                onClick={() => selectPlayer(player)} data-testid="util-marker" aria-pressed={active}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'center' }}>
                <span style={{
                  position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 30, height: 30, margin: '0 auto', borderRadius: '50%',
                  background: active ? PRIMARY : withAlpha(C.neutral[800], 0.95),
                  border: `1px solid ${active ? PRIMARY : withAlpha(C.neutral[700], 0.5)}`,
                  color: active ? C.neutral[950] : C.neutral[300], fontSize: FS['2xs'], fontWeight: 800,
                }}>
                  {slot}
                  {!active && (
                    <span style={{
                      position: 'absolute', top: -2, right: -2, width: 7, height: 7,
                      borderRadius: '50%', background: statusDotColor(player),
                    }} />
                  )}
                </span>
                <span style={{
                  display: 'block', marginTop: 2, fontSize: FS['2xs'], whiteSpace: 'nowrap',
                  color: active ? PRIMARY : C.neutral[400], fontWeight: active ? 700 : 400,
                }}>
                  {(playerName(player).split(' ').pop()) || '—'}
                </span>
              </button>
            );
          })}
        </div>
      )}



      {/* ============ DETAIL + SWAPS — scrolls under the pinned field ============ */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }} data-testid="field-detail-scroll">

        {rosterError && (
          <div style={{ padding: SP.md, fontSize: FS.sm, color: C.error }} data-testid="roster-error">
            Couldn’t load your roster. {rosterError}
          </div>
        )}

        {!selected && !rosterError && (
          <div style={{ padding: SP.lg, textAlign: 'center', fontSize: FS.sm, color: C.neutral[400] }}
               data-testid="field-empty-prompt">
            Tap a player to see their 1-for-1 swap options.
          </div>
        )}

        {selected && (
          <>
            {/* ---- tapped player's line: name, pos/team, salary, league categories ---- */}
            <div style={{ padding: `${SP.xs} ${SP.md}`, borderBottom: BORDER }} data-testid="field-detail-card">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: SP.sm }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* DD-RO-MOBILE-FIELD-RENDER-0728 wiring fix: MobilePlayerLink renders its `name` prop (it
                      does NOT read `player` or children) — the delivered `player={}` + children left the name
                      blank in prod. */}
                  <MobilePlayerLink mlbId={detailPlayer?.ids?.mlb} name={playerName(detailPlayer)} onClick={onPlayerClick}
                    style={{ fontSize: FS.base, fontWeight: 900, color: C.white }} />
                  <div style={{ fontSize: FS['2xs'], color: C.neutral[400], marginTop: 2 }}>
                    {playerPos(detailPlayer)}
                    {detailPlayer?.info?.mlb_team ? ` · ${detailPlayer.info.mlb_team}` : ''}
                  </div>
                </div>
                {playerSalary(detailPlayer) && (
                  <span style={{ fontSize: FS.sm, fontWeight: 800, color: PRIMARY, flexShrink: 0 }}>
                    {playerSalary(detailPlayer)}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: SP.md, marginTop: 3, flexWrap: 'wrap' }} data-testid="detail-stat-line">
                {displayStats(selected, isPitcherSide).map((st) => (
                  <span key={st.abbrev} style={{ fontSize: FS['2xs'], color: C.neutral[400] }}>
                    <span style={{ color: C.neutral[500] }}>{st.abbrev} </span>
                    <span style={{ color: C.white, fontWeight: 700 }}>{st.formatted}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* ---- Free Agents / Trades ---- */}
            <div style={{ display: 'flex', borderBottom: BORDER }} data-testid="swap-tabs">
              {[['free_agents', 'Free Agents'], ['trades', 'Trades']].map(([key, label]) => {
                const on = tab === key;
                return (
                  <button key={key} type="button" onClick={() => setTab(key)}
                    data-testid={`swap-tab-${key}`} aria-pressed={on}
                    style={{
                      flex: 1, padding: '3px 0', background: 'none', border: 'none',
                      borderBottom: on ? `2px solid ${PRIMARY}` : '2px solid transparent',
                      color: on ? PRIMARY : C.neutral[400],
                      fontSize: FS.xs, fontWeight: on ? 800 : 700, cursor: 'pointer',
                    }}>
                    {label}
                  </button>
                );
              })}
            </div>

            {altsLoading && (
              <div style={{ padding: SP.md, fontSize: FS.sm, color: C.neutral[400] }} data-testid="alts-loading">
                Finding swaps for {playerName(selected)}…
              </div>
            )}
            {altsError && (
              <div style={{ padding: SP.md, fontSize: FS.sm, color: C.error }} data-testid="alts-error">
                {altsError}
              </div>
            )}

            {/* ---- FA candidates: real marginal gain, honest when withheld ---- */}
            {!altsLoading && !altsError && tab === 'free_agents' && (
              <div data-testid="fa-list">
                {(alts?.alternatives || []).length === 0 ? (
                  <div style={{ padding: SP.md, fontSize: FS.sm, color: C.neutral[400] }} data-testid="fa-empty">
                    No free agent improves on {playerName(selected)} right now.
                  </div>
                ) : (
                  (alts.alternatives || []).map((alt) => {
                    const cand = alt?.player || alt;
                    const gain = alt?.score?.weighted_score ?? alt?.score?.total_points_delta ?? null;
                    return (
                      <div key={cand?.ids?.mlb || playerName(cand)}
                           style={{ display: 'flex', alignItems: 'center', gap: SP.sm,
                                    padding: `${SP.xs} ${SP.md}`, borderBottom: BORDER_SOFT }}
                           data-testid="fa-row">
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: FS.sm, fontWeight: 700, color: C.white,
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {playerName(cand)}
                          </div>
                          <div style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>
                            {playerPos(cand)}{cand?.info?.mlb_team ? ` · ${cand.info.mlb_team}` : ''}
                            {playerSalary(cand) ? ` · ${playerSalary(cand)}` : ''}
                          </div>
                        </div>
                        <span style={{ fontSize: FS.sm, fontWeight: 900, flexShrink: 0,
                                       color: gain == null ? C.neutral[500] : gain > 0 ? C.success : C.neutral[400] }}
                              data-testid="fa-gain">
                          {gain == null ? '—' : `${gain > 0 ? '+' : ''}${Number(gain).toFixed(1)}`}
                        </span>
                        <button type="button" onClick={() => requestSwap(cand, alt?.score)}
                          data-testid="fa-swap-btn"
                          style={{ flexShrink: 0, background: PRIMARY, color: C.neutral[950], border: 'none',
                                   borderRadius: R.none ?? 0, padding: `${SP.xs} ${SP.sm}`,
                                   fontSize: FS.xs, fontWeight: 800, cursor: 'pointer' }}>
                          Swap →
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* ---- trade packages: real net value + acceptance band ---- */}
            {!altsLoading && !altsError && tab === 'trades' && (
              <div data-testid="trade-list">
                {(alts?.subject_trades || []).length === 0 ? (
                  <div style={{ padding: SP.md, fontSize: FS.sm, color: C.neutral[400] }} data-testid="trade-empty">
                    No trade package featuring {playerName(selected)} nets positive value.
                  </div>
                ) : (
                  (alts.subject_trades || []).map((pkg, i) => {
                    const band = acceptanceBand(pkg?.acceptance_probability);
                    const net = pkg?.net_value ?? null;
                    return (
                      <div key={pkg?.id || i}
                           style={{ padding: `${SP.xs} ${SP.md}`, borderBottom: BORDER_SOFT }}
                           data-testid="trade-row">
                        <div style={{ display: 'flex', alignItems: 'center', gap: SP.sm }}>
                          <div style={{ flex: 1, minWidth: 0, fontSize: FS.sm, fontWeight: 700, color: C.white,
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {pkg?.headline || pkg?.summary || `with ${pkg?.partner_team_name || 'league team'}`}
                          </div>
                          <span style={{ fontSize: FS.sm, fontWeight: 900, flexShrink: 0,
                                         color: net == null ? C.neutral[500] : net > 0 ? C.success : C.neutral[400] }}
                                data-testid="trade-net">
                            {net == null ? '—' : `${net > 0 ? '+' : ''}${Number(net).toFixed(1)}`}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: SP.sm, marginTop: 3 }}>
                          <span style={{ ...pillStyle(band.color), fontSize: FS['2xs'] }} data-testid="trade-band">
                            {band.label}
                          </span>
                          {pkg?.partner_team_name && (
                            <span style={{ fontSize: FS['2xs'], color: C.neutral[500] }}>
                              vs {pkg.partner_team_name}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ---- fallback: CC's entry not wired yet → same { player, drop } straight to the sheet ---- */}
      {fallbackRec && (
        <MobileFASwapSheet
          leagueId={leagueId}
          rec={fallbackRec}
          onClose={() => setFallbackRec(null)}
          onComplete={() => { setFallbackRec(null); if (onComplete) onComplete(); }}
          onPlayerClick={onPlayerClick}
        />
      )}
    </div>
  );
}
